"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase/client";
import {
  expandRootsForAreaDatabases,
  membersForAreaRoot,
  PIPEFY_GLOBAL_AREAS_CONNECTED_REPO_ID,
  type AreaDatabaseRoot,
  type CentroEscopoRow,
} from "@/lib/pipefyN1DatabaseRoots";
import { pipefyApi } from "@/lib/api-client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Network, RefreshCw, Save, CloudUpload, Info, AlertTriangle, Plus, Unlink } from "lucide-react";
import { toast } from "sonner";

const MIGRATION_098_SQL = `BEGIN;

CREATE TABLE IF NOT EXISTS public.solis_pipefy_area_escopo_config (
  root_centro_custo_id uuid NOT NULL
    REFERENCES public.solis_centros_custo (id) ON DELETE CASCADE,
  pipefy_table_id text NOT NULL,
  pipefy_table_name text,
  updated_at timestamptz NOT NULL DEFAULT now(),
  CONSTRAINT solis_pipefy_area_escopo_config_pkey PRIMARY KEY (root_centro_custo_id)
);

CREATE INDEX IF NOT EXISTS solis_pipefy_area_escopo_config_table_idx
  ON public.solis_pipefy_area_escopo_config (pipefy_table_id);

COMMENT ON TABLE public.solis_pipefy_area_escopo_config IS
  'Database Pipefy por raiz de área (N1 ou N2 na exceção Jurídico/Compliance) — etapa 2, sync de estrutura por diretoria.';

GRANT SELECT, INSERT, UPDATE, DELETE ON public.solis_pipefy_area_escopo_config TO authenticated;
GRANT ALL ON public.solis_pipefy_area_escopo_config TO service_role;

COMMIT;`;

function isMissingConfigTableError(err: { message?: string; details?: string } | null) {
  if (!err) return false;
  const blob = `${err.message || ""} ${err.details || ""}`.toLowerCase();
  return blob.includes("solis_pipefy_area_escopo_config");
}

function supabaseSqlEditorUrl(): string | null {
  const raw = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  try {
    const host = new URL(raw || "").hostname;
    const ref = host.split(".")[0];
    return ref ? `https://supabase.com/dashboard/project/${ref}/sql/new` : null;
  } catch {
    return null;
  }
}

type EscopoConfigRow = {
  root_centro_custo_id: string;
  pipefy_table_id: string;
  pipefy_table_name: string | null;
  updated_at?: string;
};

type DatabaseItem = { id: string; name: string; description: string | null; url: string };

/** Ordem no Pipefy: Área (nome), Centro (global), ID Solis (interno), Nível — igual à edge `sync-area-escopo-pipefy`. */
async function seedEscopoPipefyTableFields(tableId: string): Promise<{ failed: string[] }> {
  const failed: string[] = [];

  const tryField = async (fn: () => Promise<unknown>, label: string) => {
    try {
      await fn();
    } catch {
      failed.push(label);
    }
  };

  await tryField(
    () =>
      pipefyApi.createField({
        tableId,
        type: "short_text",
        label: "Área",
        required: true,
        description: "Nome do centro ou subárea (Solis)",
      }),
    "Área"
  );
  await tryField(
    () =>
      pipefyApi.createField({
        tableId,
        type: "connector",
        label: "Centro (global)",
        required: true,
        description: "Registro no database global de áreas/subáreas (Pipefy) — obrigatório",
        connectedRepoId: PIPEFY_GLOBAL_AREAS_CONNECTED_REPO_ID,
      }),
    "Centro (global)"
  );
  await tryField(
    () =>
      pipefyApi.createField({
        tableId,
        type: "short_text",
        label: "ID Solis (interno)",
        required: true,
        description: "UUID do centro em solis_centros_custo — chave de sync",
      }),
    "ID Solis (interno)"
  );
  await tryField(
    () =>
      pipefyApi.createField({
        tableId,
        type: "short_text",
        label: "Nível",
        required: true,
        description: "Ex.: Nível 1, Nível 2, Nível 3 (solis_centros_custo.nivel)",
      }),
    "Nível"
  );

  return { failed };
}

export default function AreaEscopoPipefyTab() {
  const [centros, setCentros] = useState<CentroEscopoRow[]>([]);
  const [configs, setConfigs] = useState<Record<string, EscopoConfigRow>>({});
  const [databases, setDatabases] = useState<DatabaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingDb, setLoadingDb] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [draftTableId, setDraftTableId] = useState<Record<string, string>>({});
  const [configTableMissing, setConfigTableMissing] = useState(false);
  const [creatingDbId, setCreatingDbId] = useState<string | null>(null);
  const [clearingId, setClearingId] = useState<string | null>(null);

  const unlinkRootCentroFromConfig = useCallback(async (rootCentroId: string) => {
    const { error } = await supabase
      .from("solis_pipefy_area_escopo_config")
      .delete()
      .eq("root_centro_custo_id", rootCentroId);
    if (error) throw error;
    setConfigs((prev) => {
      const n = { ...prev };
      delete n[rootCentroId];
      return n;
    });
    setDraftTableId((prev) => {
      const n = { ...prev };
      delete n[rootCentroId];
      return n;
    });
  }, []);

  /** Remove linha em solis_pipefy_area_escopo_config e limpa o draft local. */
  const clearVinculo = async (root: AreaDatabaseRoot) => {
    if (configTableMissing) return;
    const hasVinculo = !!(
      draftTableId[root.centroId]?.trim() || configs[root.centroId]?.pipefy_table_id?.trim()
    );
    if (!hasVinculo) return;
    setClearingId(root.centroId);
    try {
      await unlinkRootCentroFromConfig(root.centroId);
      toast.success("Vínculo removido. Você pode criar um database novo ou escolher outro na lista.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover vínculo");
    } finally {
      setClearingId(null);
    }
  };

  const loadCentrosEConfig = useCallback(async () => {
    setLoading(true);
    setConfigTableMissing(false);
    try {
      const { data: cc, error: e1 } = await supabase
        .from("solis_centros_custo")
        .select("id, nome, nivel, diretoria_nome, area_pai_nome, pipefy_database_id")
        .eq("ativo", true);
      if (e1) throw e1;
      setCentros((cc || []) as CentroEscopoRow[]);

      const { data: cfg, error: e2 } = await supabase.from("solis_pipefy_area_escopo_config").select("*");
      if (e2) {
        if (isMissingConfigTableError(e2)) {
          setConfigTableMissing(true);
          setConfigs({});
          setDraftTableId({});
          toast.message("Tabela de configuração ainda não existe no banco", {
            description: "Execute o SQL da migração 098 no Supabase (veja o aviso acima).",
          });
          return;
        }
        throw e2;
      }
      const map: Record<string, EscopoConfigRow> = {};
      for (const r of cfg || []) {
        map[(r as EscopoConfigRow).root_centro_custo_id] = r as EscopoConfigRow;
      }
      setConfigs(map);
      const nextDraft: Record<string, string> = {};
      for (const r of cfg || []) {
        const row = r as EscopoConfigRow;
        nextDraft[row.root_centro_custo_id] = row.pipefy_table_id;
      }
      setDraftTableId(nextDraft);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDatabases = useCallback(async () => {
    setLoadingDb(true);
    try {
      const data = await pipefyApi.listDatabases({});
      if (data.success && data.databases) {
        setDatabases(data.databases);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Erro ao listar databases Pipefy");
    } finally {
      setLoadingDb(false);
    }
  }, []);

  useEffect(() => {
    loadCentrosEConfig();
    loadDatabases();
  }, [loadCentrosEConfig, loadDatabases]);

  const roots = useMemo(() => {
    const thin = centros.map((c) => ({ id: c.id, nome: c.nome, nivel: c.nivel }));
    return expandRootsForAreaDatabases(thin);
  }, [centros]);

  const rootsWithMeta = useMemo(() => {
    return roots.map((r) => {
      const members = membersForAreaRoot(r, centros);
      const semGlobal = members.filter((m) => !centros.find((c) => c.id === m.centroId)?.pipefy_database_id);
      return { r, members, semGlobal };
    });
  }, [roots, centros]);

  const pipefySuggestedName = (root: AreaDatabaseRoot) =>
    `Area — ${root.nome}`.slice(0, 100);

  /** Cria database no Pipefy e os campos: Área, Centro global, ID Solis, Nível. */
  const createDatabaseForRoot = async (root: AreaDatabaseRoot) => {
    if (configTableMissing) {
      toast.error("Crie a tabela solis_pipefy_area_escopo_config no Supabase antes.");
      return;
    }
    let linkedId = (draftTableId[root.centroId] ?? configs[root.centroId]?.pipefy_table_id ?? "").trim();

    if (linkedId && databases.some((d) => d.id === linkedId)) {
      toast.message("Esta raiz já tem database selecionado", {
        description: "Limpe o vínculo primeiro ou escolha outro database no dropdown.",
      });
      return;
    }

    if (linkedId && !loadingDb && !databases.some((d) => d.id === linkedId)) {
      try {
        await unlinkRootCentroFromConfig(root.centroId);
        toast.message("Vínculo antigo removido (esse database não existe mais no Pipefy).");
      } catch (err: unknown) {
        toast.error(err instanceof Error ? err.message : "Erro ao limpar vínculo órfão");
        return;
      }
      linkedId = "";
    }

    setCreatingDbId(root.centroId);
    try {
      const name = pipefySuggestedName(root);
      const createResult = (await pipefyApi.createDatabase({
        name,
        description: `Estrutura por área / etapa 2 (${root.origem === "n2_split" ? "raiz N2" : "raiz N1"}).`,
      })) as { success?: boolean; table?: { id: string; name: string }; error?: string };
      if (!createResult.success || !createResult.table?.id) {
        throw new Error(createResult.error || "Resposta inválida ao criar database");
      }
      const newTableId = createResult.table.id;
      const { failed } = await seedEscopoPipefyTableFields(newTableId);
      if (failed.length > 0) {
        toast.warning(`Database criado, mas falhou criar campo(s): ${failed.join(", ")}`, {
          description: "Tente Sincronizar (a edge tenta criar campos que faltam) ou verifique permissões do Pipefy.",
        });
      }

      await loadDatabases();
      setDraftTableId((d) => ({ ...d, [root.centroId]: newTableId }));

      const { error: upErr } = await supabase.from("solis_pipefy_area_escopo_config").upsert(
        {
          root_centro_custo_id: root.centroId,
          pipefy_table_id: newTableId,
          pipefy_table_name: createResult.table.name ?? name,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "root_centro_custo_id" }
      );
      if (upErr) throw upErr;
      setConfigs((prev) => ({
        ...prev,
        [root.centroId]: {
          root_centro_custo_id: root.centroId,
          pipefy_table_id: newTableId,
          pipefy_table_name: createResult.table.name ?? name,
        },
      }));
      toast.success(
        failed.length === 0
          ? `Database "${createResult.table.name}" criado com Área, Centro global, ID Solis e Nível. Sincronize para popular registros.`
          : `Database "${createResult.table.name}" criado e vínculo salvo. Corrija os campos (${failed.join(", ")}) ou use Sincronizar.`
      );
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar database no Pipefy");
    } finally {
      setCreatingDbId(null);
    }
  };

  const saveConfig = async (root: AreaDatabaseRoot) => {
    if (configTableMissing) {
      toast.error("Crie a tabela solis_pipefy_area_escopo_config no Supabase antes de salvar.");
      return;
    }
    const tableId = draftTableId[root.centroId]?.trim();
    if (!tableId) {
      toast.error("Selecione um database Pipefy");
      return;
    }
    if (!databases.some((d) => d.id === tableId)) {
      toast.error("Escolha um database que apareça na lista (atualize «Databases») ou limpe o vínculo.");
      return;
    }
    const dbMeta = databases.find((d) => d.id === tableId);
    setSavingId(root.centroId);
    try {
      const { error } = await supabase.from("solis_pipefy_area_escopo_config").upsert(
        {
          root_centro_custo_id: root.centroId,
          pipefy_table_id: tableId,
          pipefy_table_name: dbMeta?.name ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "root_centro_custo_id" }
      );
      if (error) throw error;
      setConfigs((prev) => ({
        ...prev,
        [root.centroId]: {
          root_centro_custo_id: root.centroId,
          pipefy_table_id: tableId,
          pipefy_table_name: dbMeta?.name ?? null,
        },
      }));
      toast.success("Database da área salvo.");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSavingId(null);
    }
  };

  const runSync = async (root: AreaDatabaseRoot) => {
    const tableId = (draftTableId[root.centroId] || configs[root.centroId]?.pipefy_table_id)?.trim();
    if (!tableId) {
      toast.error("Configure e salve o database Pipefy antes de sincronizar.");
      return;
    }
    if (!databases.some((d) => d.id === tableId)) {
      toast.error("O database vinculado não está na lista do Pipefy (apagado?). Limpe o vínculo ou atualize «Databases».");
      return;
    }
    setSyncingId(root.centroId);
    try {
      const res = (await pipefyApi.syncAreaEscopo({
        rootCentroId: root.centroId,
        pipefyTableId: tableId,
      })) as {
        success?: boolean;
        synced?: number;
        errors?: string[];
        details?: unknown[];
      };
      if (res.errors?.length) {
        toast.message(`Sync concluído com avisos (${res.synced ?? 0} registros).`, {
          description: res.errors.slice(0, 5).join("; "),
        });
      } else {
        toast.success(`Sincronizado: ${res.synced ?? 0} registro(s).`);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Erro na sincronização");
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Network className="h-5 w-5" />
            Estrutura por área (etapa 2)
          </h3>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Uma tabela Pipefy por raiz de diretoria (N1 ou N2 na exceção Jurídico/Compliance), com registros da
            árvore e connector para o database global de centros (<code className="text-xs">bvoPUmtK</code>).{" "}
            <strong>Criar database</strong> cria os campos na ordem: nome em <strong>Área</strong>, link em{" "}
            <strong>Centro (global)</strong>, UUID em <strong>ID Solis</strong> e texto em <strong>Nível</strong>.{" "}
            <strong>Sincronizar</strong> preenche os registros (e cria campo que faltar).
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={loadDatabases} disabled={loadingDb}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loadingDb ? "animate-spin" : ""}`} />
            Databases
          </Button>
          <Button variant="outline" size="sm" onClick={loadCentrosEConfig} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Dados
          </Button>
        </div>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Total de raízes esperado</AlertTitle>
        <AlertDescription>
          Lista derivada de <code className="text-xs">expandRootsForAreaDatabases</code> — em geral 8 N1 + 2 N2
          (Jurídico e Compliance). Centros sem <code className="text-xs">pipefy_database_id</code> no cadastro
          global aparecem no aviso “sem registro global”.
        </AlertDescription>
      </Alert>

      {configTableMissing && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Falta criar a tabela no Postgres</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>
              No Supabase Dashboard, abra o <strong>SQL Editor</strong>, cole e execute o script abaixo (migração{" "}
              <code className="text-xs">098_solis_pipefy_area_escopo_config</code>). Depois clique em{" "}
              <strong>Dados</strong> para recarregar.
            </p>
            {supabaseSqlEditorUrl() && (
              <p>
                <a
                  href={supabaseSqlEditorUrl()!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline font-medium"
                >
                  Abrir SQL Editor no Supabase
                </a>
              </p>
            )}
            <pre className="mt-2 max-h-[min(55vh,28rem)] overflow-auto rounded-md border bg-muted p-3 text-[11px] leading-relaxed font-mono whitespace-pre-wrap select-all">
              {MIGRATION_098_SQL}
            </pre>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Raízes e databases</CardTitle>
          <CardDescription>
            Por raiz: <strong>Criar database</strong> usa o nome sugerido <code className="text-xs">Area — …</code>{" "}
            e cria, nesta ordem: <strong>Área</strong> (nome), connector <strong>Centro (global)</strong>{" "}
            (<code className="text-xs">bvoPUmtK</code>), <strong>ID Solis</strong> (técnico) e <strong>Nível</strong>{" "}
            (ex. Nível 1/2/3). Ou escolha um database existente no dropdown. Depois <strong>Sincronizar</strong> preenche os
            registros. Raiz N1 usa <code className="text-xs">diretoria_nome</code>; N2 split usa{" "}
            <code className="text-xs">area_pai_nome</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Carregando centros…
            </div>
          ) : (
            rootsWithMeta.map(({ r, members, semGlobal }) => {
              const saved = configs[r.centroId];
              const linkedId =
                (draftTableId[r.centroId] ?? saved?.pipefy_table_id ?? "").trim();
              const linkedKnown = linkedId !== "" && databases.some((d) => d.id === linkedId);
              const selectValue = linkedKnown ? linkedId : "";
              const orphanLink = linkedId !== "" && !loadingDb && !linkedKnown;

              return (
                <div
                  key={r.centroId}
                  className="flex flex-col gap-3 rounded-lg border p-4 md:flex-row md:items-start md:justify-between"
                >
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{r.nome}</span>
                      <Badge variant={r.origem === "n2_split" ? "secondary" : "default"}>
                        {r.origem === "n2_split" ? "Raiz N2 (split)" : "Raiz N1"}
                      </Badge>
                      <Badge variant="outline">{members.length} membro(s)</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {members.slice(0, 6).map((m) => m.nome).join(" · ")}
                      {members.length > 6 ? ` … +${members.length - 6}` : ""}
                    </p>
                    {orphanLink && (
                      <p className="text-xs text-amber-800 dark:text-amber-300 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-2 py-1">
                        Vínculo órfão: o ID salvo não existe mais no Pipefy (<code className="text-[10px]">{linkedId}</code>
                        ). Use <strong>Limpar vínculo</strong> ou <strong>Criar database</strong> (limpa automaticamente).
                      </p>
                    )}
                    {semGlobal.length > 0 && (
                      <p className="text-xs text-amber-700 dark:text-amber-400">
                        Sem ID no DB global Pipefy: {semGlobal.map((m) => m.nome).join(", ")}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full md:w-[min(100%,420px)] shrink-0">
                    <Select
                      key={`${r.centroId}-${linkedId}-${selectValue}`}
                      value={selectValue}
                      onValueChange={(v) => setDraftTableId((d) => ({ ...d, [r.centroId]: v }))}
                      disabled={loadingDb}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Database Pipefy…" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {databases.map((db) => (
                          <SelectItem key={db.id} value={db.id}>
                            <span className="truncate">{db.name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => createDatabaseForRoot(r)}
                        disabled={
                          configTableMissing ||
                          loadingDb ||
                          creatingDbId !== null ||
                          clearingId !== null ||
                          savingId === r.centroId ||
                          linkedKnown
                        }
                        title={
                          linkedKnown
                            ? "Limpe o vínculo primeiro se quiser criar outro database"
                            : `Criar no Pipefy: ${pipefySuggestedName(r)}`
                        }
                      >
                        {creatingDbId === r.centroId ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Plus className="h-3 w-3 mr-1" />
                        )}
                        Criar database
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => clearVinculo(r)}
                        disabled={
                          configTableMissing ||
                          clearingId !== null ||
                          creatingDbId !== null ||
                          !linkedId
                        }
                        title="Remove o vínculo no Supabase (não apaga database no Pipefy)"
                      >
                        {clearingId === r.centroId ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Unlink className="h-3 w-3 mr-1" />
                        )}
                        Limpar vínculo
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => saveConfig(r)}
                        disabled={
                          configTableMissing ||
                          savingId === r.centroId ||
                          !linkedId ||
                          !databases.some((d) => d.id === linkedId)
                        }
                      >
                        {savingId === r.centroId ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <Save className="h-3 w-3 mr-1" />
                        )}
                        Salvar vínculo
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => runSync(r)}
                        disabled={
                          syncingId === r.centroId || !linkedId || !linkedKnown || loadingDb
                        }
                      >
                        {syncingId === r.centroId ? (
                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        ) : (
                          <CloudUpload className="h-3 w-3 mr-1" />
                        )}
                        Sincronizar
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
